import { NS_ERROR } from '@/i18n/namespaces';
import { useTranslation } from 'react-i18next';
import { ObjectSchema, bool, mixed, number, object, string } from 'yup';
import { ACCEPT_TYPES, MAX_FILE_SIZE, SOF_OTHERS } from '../constants';
import { ErrorMessage, Gender, Relationship } from '../enums';
import {
  AddressFormValues,
  ApplicationFormValues,
  BankDetailFormValues,
  BeneficialFormValues,
  PersonalDetailFormValues,
} from '../types/form';
import { capitalizeEachWord } from '@/utils/validators';

export const useApplicationSchema = () => {
  const { t } = useTranslation(NS_ERROR);
  const fileSchema = string()
    .required(ErrorMessage.Required)
    .test('file-type', ErrorMessage.UnsupportedFileType, (value) => {
      if (!value) return false;
      const match = value.match(/^data:(.*?);base64,/);
      if (!match) return false;
      const mimeType = match[1];
      const acceptedTypes = ACCEPT_TYPES.split(',');
      return acceptedTypes.includes(mimeType);
    })
    .test('file-size', ErrorMessage.MaxFileSize, (value) => {
      if (!value) return false;
      const base64Data = value.split(',')[1] || value;
      try {
        const sizeInBytes = atob(base64Data).length;
        return sizeInBytes <= MAX_FILE_SIZE;
      } catch {
        return false;
      }
    });

  const addressSchema: ObjectSchema<AddressFormValues> = object({
    address1: string()
      .required(ErrorMessage.Required)
      .min(5, t(ErrorMessage.MinLength, { min: 5 }))
      .max(150, t(ErrorMessage.MaxLength, { max: 150 })),
    address2: string()
      .optional()
      .default(undefined)
      .test(
        'min-length',
        t(ErrorMessage.MinLength, { min: 5 }),
        (value) => !value || value.length >= 5,
      )
      .test(
        'max-length',
        t(ErrorMessage.MaxLength, { max: 150 }),
        (value) => !value || value.length <= 150,
      ),
    postcode: string()
      .required(ErrorMessage.Required)
      .matches(/^[0-9]{5}$/, ErrorMessage.Postcode),
    city: string()
      .required(ErrorMessage.Required)
      .min(2, t(ErrorMessage.MinLength, { min: 2 }))
      .max(100, t(ErrorMessage.MaxLength, { max: 100 })),
    state: string().required(ErrorMessage.Required),
    country: string().required(ErrorMessage.Required),
  });

  const personalDetailPOSchema: ObjectSchema<PersonalDetailFormValues> = object(
    {
      title: string().required(ErrorMessage.Required),
      maritalStatus: string().required(ErrorMessage.Required),
      purposeOfTransaction: string().required(ErrorMessage.Required),
      name: string()
        .required(ErrorMessage.Required)
        .min(3, ErrorMessage.Name)
        .max(150, ErrorMessage.Name),
      nric: string()
        .required(ErrorMessage.Required)
        .matches(/^[0-9]{12}$/, ErrorMessage.NRIC)
        .test('valid-state-code', ErrorMessage.StateCode, (value) => {
          const nricStateCode = value?.slice(6, 8);
          return nricStateCode !== '00';
        })
        .test('valid-last-4-digits', ErrorMessage.Last4Digits, (value) => {
          const nricLast4Digits = value?.slice(8, 12);
          return nricLast4Digits !== '0000';
        }),
      gender: mixed<Gender>()
        .oneOf(Object.values(Gender))
        .required(ErrorMessage.Required),
      nationality: string().required(ErrorMessage.Required),
      mobile: string()
        .required(ErrorMessage.Required)
        .matches(/^[0-9]+$/, ErrorMessage.Mobile)
        .min(3, ErrorMessage.Mobile)
        .max(18, ErrorMessage.Mobile),
      mobilePrefix: string().required(ErrorMessage.Required),
      religion: string().notRequired(),
      race: string().notRequired(),
      email: string()
        .email()
        .required(ErrorMessage.Required)
        .min(5, ErrorMessage.Email)
        .max(100, ErrorMessage.Email),
      occupation: string().required(ErrorMessage.Required),
      employer: string()
        .min(3, ErrorMessage.Employer)
        .max(150, ErrorMessage.Employer),
      income: number()
        .transform((_, val) => (val ? Number(val) : undefined))
        .required(ErrorMessage.Required)
        .min(1, ErrorMessage.Income)
        .max(999999999.99, ErrorMessage.Income)
        .test(
          'decimal-places',
          ErrorMessage.Income,
          (value) =>
            value === undefined || /^\d+(\.\d{1,2})?$/.test(String(value)),
        ),
      uploadedNric: object({
        frontSideImg: fileSchema,
        backSideImg: fileSchema,
      }),
      fundSource: string().required(ErrorMessage.Required),
      wealthSource: string().required(ErrorMessage.Required),
      fundSourceOthers: string().when('fundSource', {
        is: SOF_OTHERS,
        then: (schema) =>
          schema
            .required(ErrorMessage.Required)
            .min(1, t(ErrorMessage.MinLength, { min: 1 }))
            .max(150, t(ErrorMessage.MaxLength, { max: 150 }))
            .matches(/^[a-zA-Z0-9 \-.,'()/&]*$/, ErrorMessage.AlphaNumeric),
        otherwise: (schema) => schema.notRequired().default(undefined),
      }),
      isSameAddress: bool().default(false),
      homeAddress: addressSchema,
      mailingAddress: addressSchema.when('isSameAddress', {
        is: false,
        then: (schema) => schema.required(),
        otherwise: () => object().notRequired().default(undefined),
      }),
    },
  );

  const personalDetailLISchema: ObjectSchema<PersonalDetailFormValues> = object(
    {
      title: string().required(ErrorMessage.Required),
      maritalStatus: string().required(ErrorMessage.Required),
      purposeOfTransaction: string().required(ErrorMessage.Required),
      name: string()
        .required(ErrorMessage.Required)
        .min(3, ErrorMessage.Name)
        .max(150, ErrorMessage.Name),
      nric: string()
        .required(ErrorMessage.Required)
        .matches(/^[0-9]{12}$/, ErrorMessage.NRIC)
        .test('valid-state-code', ErrorMessage.StateCode, (value) => {
          const nricStateCode = value?.slice(6, 8);
          return nricStateCode !== '00';
        })
        .test('valid-last-4-digits', ErrorMessage.Last4Digits, (value) => {
          const nricLast4Digits = value?.slice(8, 12);
          return nricLast4Digits !== '0000';
        }),
      gender: mixed<Gender>()
        .oneOf(Object.values(Gender))
        .required(ErrorMessage.Required),
      nationality: string().required(ErrorMessage.Required),
      mobile: string()
        .required(ErrorMessage.Required)
        .matches(/^[0-9]+$/, ErrorMessage.Mobile)
        .min(3, ErrorMessage.Mobile)
        .max(18, ErrorMessage.Mobile),
      mobilePrefix: string().required(ErrorMessage.Required),
      religion: string().notRequired(),
      race: string().notRequired(),
      email: string()
        .email()
        .required(ErrorMessage.Required)
        .min(5, ErrorMessage.Email)
        .max(100, ErrorMessage.Email),
      occupation: string().required(ErrorMessage.Required),
      employer: string()
        .when('$relationship', {
          is: (value: Relationship) =>
            value === Relationship.Son || value === Relationship.Daughter,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        })
        .min(3, ErrorMessage.Employer)
        .max(150, ErrorMessage.Employer),
      income: number()
        .when('$relationship', {
          is: (value: Relationship) =>
            value === Relationship.Son || value === Relationship.Daughter,
          then: (schema) => schema.notRequired().nullable(),
          otherwise: (schema) => schema.required(),
        })
        .required(ErrorMessage.Required)
        .transform((_, val) => (val ? Number(val) : undefined))
        .min(1, ErrorMessage.Income)
        .max(999999999.99, ErrorMessage.Income)
        .test(
          'decimal-places',
          ErrorMessage.Income,
          (value) =>
            value === undefined || /^\d+(\.\d{1,2})?$/.test(String(value)),
        ),
      uploadedNric: object({
        frontSideImg: fileSchema,
        backSideImg: fileSchema,
      }),
      isSameAddress: bool().default(false),
      isSameAddressAsPO: bool().default(false),
      homeAddress: addressSchema.when('isSameAddressAsPO', {
        is: false,
        then: (schema) => schema.required(),
        otherwise: () => object().notRequired().default(undefined),
      }),
      mailingAddress: addressSchema.when('isSameAddress', {
        is: false,
        then: (schema) => schema.required(),
        otherwise: () => object().notRequired().default(undefined),
      }),
    },
  );

  const beneficialDetailSchema: ObjectSchema<BeneficialFormValues> = object({
    purposeOfTransaction: string().required(ErrorMessage.Required),
    relationship: mixed<Relationship>().required(ErrorMessage.Required),
    name: string()
      .required(ErrorMessage.Required)
      .min(3, ErrorMessage.Name)
      .max(150, ErrorMessage.Name),
    nric: string()
      .required(ErrorMessage.Required)
      .matches(/^[0-9]{12}$/, ErrorMessage.NRIC)
      .test('valid-state-code', ErrorMessage.StateCode, (value) => {
        const nricStateCode = value?.slice(6, 8);
        return nricStateCode !== '00';
      })
      .test('valid-last-4-digits', ErrorMessage.Last4Digits, (value) => {
        const nricLast4Digits = value?.slice(8, 12);
        return nricLast4Digits !== '0000';
      }),
    nationality: string().required(ErrorMessage.Required),
    mobile: string()
      .required(ErrorMessage.Required)
      .matches(/^[0-9]+$/, ErrorMessage.Mobile)
      .min(3, ErrorMessage.Mobile)
      .max(18, ErrorMessage.Mobile),
    mobilePrefix: string().required(ErrorMessage.Required),
    religion: string().required(),
    race: string().required(),
    email: string()
      .email()
      .required(ErrorMessage.Required)
      .min(5, ErrorMessage.Email)
      .max(100, ErrorMessage.Email),
    occupation: string().required(ErrorMessage.Required),
    employer: string()
      .required(ErrorMessage.Required)
      .min(3, ErrorMessage.Employer)
      .max(150, ErrorMessage.Employer),
    income: number()
      .transform((_, val) => (val ? Number(val) : undefined))
      .required(ErrorMessage.Required)
      .min(1, ErrorMessage.Income)
      .max(999999999.99, ErrorMessage.Income)
      .test(
        'decimal-places',
        ErrorMessage.Income,
        (value) =>
          value === undefined || /^\d+(\.\d{1,2})?$/.test(String(value)),
      ),
    homeAddress: addressSchema,
    mailingAddress: addressSchema.when('isSameAddress', {
      is: false,
      then: (schema) => schema.required(),
      otherwise: () => object().notRequired().default(undefined),
    }),
    isSameAddress: bool().default(false),
  });

  const bankDetailSchema: ObjectSchema<BankDetailFormValues> = object({
    fullname: string()
      .required(ErrorMessage.Required)
      .min(3, ErrorMessage.Name)
      .max(150, ErrorMessage.Name)
      .transform((value) => capitalizeEachWord(value)),
    bankName: string().required(ErrorMessage.Required),
    accountNo: string()
      .required(ErrorMessage.Required)
      .matches(/^[0-9]+$/, ErrorMessage.BankAccountNumber)
      .min(8, ErrorMessage.BankAccountNumber)
      .max(20, ErrorMessage.AccountNo),
  });

  const applicationSchema: ObjectSchema<ApplicationFormValues> = object({
    policy_owner: object({
      personalDetail: personalDetailPOSchema,
    }),
    life_insured: object({
      personalDetail: personalDetailLISchema,
    })
      .notRequired()
      .default(null),
    bankDetail: bankDetailSchema,
    otherBeneficial: bool()
      .oneOf([true], ErrorMessage.Required)
      .required(ErrorMessage.Required),
    // ETQRM-11565
    // beneficialDetail: beneficialDetailSchema.when('otherBeneficial', {
    //   is: true,
    //   then: (schema) => schema.required(),
    //   otherwise: () => object().notRequired().default(undefined),
    // }),
  });
  return {
    applicationSchema,
  };
};
